<template>
  <div class="offer _modal">
    <div class="_modal-overlay" @click.self="closePreviewSkinModal"></div>
    <div class="offer__inner _modal-inner">
      <div class="_modal-close" @click="closePreviewSkinModal"></div>
      <div class="offer__content">
        <!-- STEP 1: Offer Details / Confirmation -->
        <div v-if="step === 1">
          <div class="offer__title _h3">
            <span v-if="item.status === 'offer'">{{
              $t("confirm selling")
            }}</span>
            <span v-else-if="item.status === 'ready_for_payout'">{{
              $t("Request Payout")
            }}</span>
            <span v-else>{{ $t("Offer Details") }}</span>
          </div>

          <div class="offer__subtitle">
            {{ $t("You have received an offer for") }}
          </div>

          <div class="offer__about">
            <div class="offer__name _md">
              {{ item.title }}
            </div>
          </div>

          <!-- PRICE DISPLAY LOGIC -->
          <div class="offer__prices">
            <!-- SCENARIO A: Service Price Exists (Show Both) -->
            <template v-if="item.service_price">
              <!-- Your Price Row -->
              <div class="offer__price-row">
                <div class="offer__subtitle offer__subtitle_label">
                  {{ $t("Your Price") }}:
                </div>
                <div class="offer__price-wrapper">
                  <div class="offer__price">
                    <div
                      class="offer__price-number offer__price-number_regular _l"
                    >
                      {{ formatPrice(item.user_price) }}
                    </div>
                    <div class="offer__price-sumbol _l">
                      {{ item.currency }}
                    </div>
                  </div>
                  <div class="offer__divider" v-if="hasUserInternal">/</div>
                  <div
                    v-if="hasUserInternal"
                    class="offer__price offer__price-coin"
                  >
                    <div class="offer__price-number _l">
                      {{ formatPrice(item.user_internal_price ?? item.internal_price) }}
                    </div>
                    <div class="offer__price-icon _svg-icon">
                      <img src="@/assets/images/coin.svg" alt="" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Our Price Row -->
              <div class="offer__price-row">
                <div class="offer__subtitle offer__subtitle_label">
                  {{ $t("Our Price") }}:
                </div>
                <div class="offer__price-wrapper offer__price-wrapper_service">
                  <div class="offer__service-price">
                    <div
                      class="offer__service-price-number offer__service-price-number_regular _l"
                    >
                      {{ formatPrice(item.service_price) }}
                    </div>
                    <div class="offer__service-price-sumbol _l">
                      {{ item.currency }}
                    </div>
                  </div>
                  <div class="offer__divider" v-if="hasServiceInternal">/</div>
                  <div
                    v-if="hasServiceInternal"
                    class="offer__service-price offer__service-price-coin"
                  >
                    <div class="offer__service-price-number _l">
                      {{ formatPrice(serviceInternalValue) }}
                    </div>
                    <div class="offer__service-price-icon _svg-icon">
                      <img src="@/assets/images/coin.svg" alt="" />
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- SCENARIO B: Service Price DOES NOT Exist (Show Single Price) -->
            <template v-else>
              <div class="offer__price-row">
                <div class="offer__subtitle offer__subtitle_label">
                  {{ $t("Price") }}:
                </div>
                <div class="offer__price-wrapper">
                  <div class="offer__price">
                    <div
                      class="offer__price-number offer__price-number_regular _l"
                    >
                      {{ formatPrice(item.user_price) }}
                    </div>
                    <div class="offer__price-sumbol _l">
                      {{ item.currency }}
                    </div>
                  </div>
                  <div class="offer__divider" v-if="hasUserInternal">/</div>
                  <div
                    v-if="hasUserInternal"
                    class="offer__price offer__price-coin"
                  >
                    <div class="offer__price-number _l">
                      {{ formatPrice(item.user_internal_price ?? item.internal_price) }}
                    </div>
                    <div class="offer__price-icon _svg-icon">
                      <img src="@/assets/images/coin.svg" alt="" />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- MESSAGES -->
          <p v-if="success" class="offer__success">{{ success }}</p>
          <p v-if="error" class="offer__error">{{ error }}</p>

          <!-- BUTTONS -->
          <!-- Show Decline/Accept only for status "offer" -->
          <div
            v-if="step === 1 && item.status === 'offer'"
            class="offer__actions"
          >
            <BaseButton
              type="submit"
              :disabled="isSubmitting"
              class="offer__submit _btn-bordered"
              @click="declineOffer"
            >
              {{ $t("Decline") }}
            </BaseButton>
            <BaseButton
              type="submit"
              :disabled="isSubmitting"
              class="offer__submit _btn-primary"
              @click="acceptOffer"
            >
              {{ $t("accept offer") }}
            </BaseButton>
          </div>

          <!-- Show PAY only for status "ready_for_payout" -->
          <div
            v-if="step === 1 && item.status === 'ready_for_payout'"
            class="offer__actions"
          >
            <BaseButton
              type="submit"
              :disabled="isSubmitting"
              class="offer__submit _btn-primary"
              @click="goToStep2"
            >
              PAY
            </BaseButton>
          </div>
          <!-- For 'payout_processing', 'approved', etc. no buttons are shown, just details. -->
        </div>

        <!-- STEP 2: Personal Data -->
        <div v-if="step === 2">
          <div class="offer__title _h3">Personal Data</div>
          <div class="offer__form">
            <BaseInput
              v-model="contactDetails.email"
              label="Email"
              className="offer__input"
            />
            <BaseInput
              v-model="contactDetails.name"
              label="Name"
              className="offer__input"
            />
            <BaseInput
              v-model="contactDetails.surname"
              label="Surname"
              className="offer__input"
            />
            <BaseInput
              v-model="contactDetails.phone"
              label="Phone"
              className="offer__input"
            />
            <div class="input offer__input">
              <label class="input__label">Country</label>
              <select v-model="contactDetails.country" class="input__field">
                <option v-for="c in countries" :key="c.id" :value="c.title">
                  {{ c.title }}
                </option>
              </select>
            </div>
            <BaseInput
              v-model="contactDetails.city"
              label="City"
              className="offer__input"
            />
            <BaseInput
              v-model="contactDetails.address"
              label="Address"
              className="offer__input"
            />
            <BaseInput
              v-model="contactDetails.postCode"
              label="Post Code"
              className="offer__input"
            />
          </div>
          <div class="offer__actions">
            <BaseButton
              class="offer__submit _btn-primary"
              :disabled="isSubmitting"
              @click="payCard"
              >CARD</BaseButton
            >
            <BaseButton
              class="offer__submit _btn-bordered"
              :disabled="isSubmitting"
              @click="goToSepa"
              >SEPA</BaseButton
            >
          </div>
        </div>

        <!-- STEP 3: SEPA Details -->
        <div v-if="step === 3">
          <div class="offer__title _h3">SEPA Details</div>
          <div class="offer__form">
            <BaseInput
              v-model="sepaDetails.account_number"
              label="IBAN"
              className="offer__input"
            />
            <BaseInput
              v-model="sepaDetails.first_name"
              label="First Name"
              className="offer__input"
            />
            <BaseInput
              v-model="sepaDetails.last_name"
              label="Last Name"
              className="offer__input"
            />
          </div>
          <div class="offer__actions">
            <BaseButton
              class="offer__submit _btn-primary"
              :disabled="isSubmitting"
              @click="submitSepa"
              >Confirm</BaseButton
            >
            <BaseButton
              class="offer__submit _btn-bordered"
              :disabled="isSubmitting"
              @click="goBack"
              >Back</BaseButton
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import BaseButton from "./base/BaseButton.vue";
import BaseInput from "./base/BaseInput.vue";

export default {
  name: "offerSkinModal",
  props: {
    item: {
      type: Object,
      required: true,
    },
    refreshOfferHistory: {
      type: Function,
    },
    user: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    BaseButton,
    BaseInput,
  },
  data() {
    return {
      isSubmitting: false,
      success: "",
      error: "",
      step: 1,
      countries: [],
      contactDetails: {
        email: "",
        name: "",
        surname: "",
        phone: "",
        country: "",
        city: "",
        address: "",
        postCode: "",
      },
      sepaDetails: {
        account_number: "",
        first_name: "",
        last_name: "",
      },
    };
  },
  computed: {
    ...mapGetters("app", ["currencySymbol", "currencyValue"]),
    hasUserInternal() {
      return Boolean(
        this.item?.user_internal_price ?? this.item?.internal_price
      );
    },
    hasServiceInternal() {
      return Boolean(
        this.item?.service_internal_price ??
          this.item?.user_internal_price ??
          this.item?.internal_price
      );
    },
    serviceInternalValue() {
      return (
        this.item?.service_internal_price ??
        this.item?.user_internal_price ??
        this.item?.internal_price ??
        0
      );
    },
  },
  mounted() {
    this.isSubmitting = false;
    console.log("item", this.item);
    this.fetchCountries();
    // Automatically go to payout step if ready
    if (this.item.status === "ready_for_payout") {
      this.goToStep2();
    }
  },
  methods: {
    openSignIn() {
      this.$router.push("/signin");
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === "") {
        return "0.00";
      }
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) {
        return "0.00";
      }
      return numberValue.toFixed(2);
    },
    closePreviewSkinModal() {
      this.$emit("closePreviewSkinModal");
    },
    async fetchCountries() {
      try {
        const res = await this.$http.get("countries");
        if (res.data && res.data.payload) {
          this.countries = res.data.payload;
        }
      } catch (err) {
        console.error("Failed to fetch countries", err);
      }
    },
    goBack() {
      this.step = 2;
      this.error = "";
      this.success = "";
    },
    goToStep2() {
      this.step = 2;
      if (this.user) {
        this.contactDetails.email = this.user.email || "";
        this.contactDetails.name = this.user.name || "";
        this.contactDetails.surname = this.user.surname || "";
        this.contactDetails.phone = this.user.phone || "";
        this.contactDetails.country = this.user.country || "";
        this.contactDetails.city = this.user.city || "";
        this.contactDetails.address = this.user.address || "";
        this.contactDetails.postCode = this.user.zip || "";
      }
    },
    validateContactDetails() {
      const requiredFields = [
        "email",
        "name",
        "surname",
        "phone",
        "country",
        "city",
        "address",
        "postCode",
      ];
      const missingFields = requiredFields.filter(
        (field) => !this.contactDetails[field]
      );

      if (missingFields.length > 0) {
        const msg = this.$t("Please fill in all required fields");
        this.error = msg;
        if (this.$toast) this.$toast.error(msg);
        return false;
      }
      this.error = "";
      return true;
    },
    handleApiError(err) {
      let message = "Something went wrong";
      if (err.response && err.response.data) {
        if (err.response.data.errors) {
          message = Object.values(err.response.data.errors).flat().join("\n");
        } else if (err.response.data.message) {
          message = err.response.data.message;
        }
      } else if (err.message) {
        message = err.message;
      }

      this.error = message;
      if (this.$toast) this.$toast.error(message);
    },
    async payCard() {
      if (!this.validateContactDetails()) return;

      this.isSubmitting = true;
      this.error = "";
      try {
        const payload = {
          id: this.item.id,
          ...this.contactDetails,
          currency: this.item.currency || "EUR",
        };
        const res = await this.$http.post(
          "offer/request-payout/card-form",
          payload
        );
        if (res.data.status === "OK" && res.data.redirect_url) {
          window.location.href = res.data.redirect_url;
        } else {
          this.error = res.data.message || "Error initiating card payment";
          if (this.$toast) this.$toast.error(this.error);
        }
      } catch (err) {
        this.handleApiError(err);
      } finally {
        this.isSubmitting = false;
      }
    },
    goToSepa() {
      if (!this.validateContactDetails()) return;

      this.step = 3;
      if (!this.sepaDetails.first_name)
        this.sepaDetails.first_name = this.contactDetails.name;
      if (!this.sepaDetails.last_name)
        this.sepaDetails.last_name = this.contactDetails.surname;
    },
    async submitSepa() {
      this.isSubmitting = true;
      this.error = "";
      try {
        const payload = {
          id: this.item.id,
          ...this.contactDetails,
          ...this.sepaDetails,
        };
        const res = await this.$http.post(
          "offer/request-payout/sepa-manual",
          payload
        );
        if (res.data.status === "OK") {
          this.success =
            res.data.message || "SEPA payout requested successfully";
          if (typeof this.refreshOfferHistory === "function") {
            this.refreshOfferHistory();
          }
          setTimeout(() => {
            this.closePreviewSkinModal();
          }, 2000);
        } else {
          this.error = res.data.message || "Error requesting SEPA payout";
          if (this.$toast) this.$toast.error(this.error);
        }
      } catch (err) {
        this.handleApiError(err);
      } finally {
        this.isSubmitting = false;
      }
    },
    async acceptOffer() {
      if (!this.item || !this.item.id) return;
      this.isSubmitting = true;
      this.success = "";
      this.error = "";
      if (typeof this.refreshOfferHistory === "function") {
        this.refreshOfferHistory();
      }
      try {
        const res = await this.$http.post("offer/accept", { id: this.item.id });

        if (res.data.status === "OK") {
          this.success =
            res.data.message ||
            this.$t("Offer accepted. We will contact you for the trade.");

          this.$emit("offerAccepted", this.item.id);

          if (typeof this.refreshOfferHistory === "function") {
            this.refreshOfferHistory();
          }
          setTimeout(() => {
            this.closePreviewSkinModal();
          }, 400);
        } else {
          this.error = res.data.message || this.$t("Something went wrong.");
        }
      } catch (err) {
        if (err.response && err.response.status === 401) {
          this.$emit("unauthorized");
        }
        this.error = err.response.data?.message;
      } finally {
        this.isSubmitting = false;
      }
    },
    async declineOffer() {
      if (!this.item || !this.item.id) return;
      this.isSubmitting = true;
      this.success = "";
      this.error = "";

      try {
        const res = await this.$http.post("offer/decline", {
          id: this.item.id,
        });

        if (res.data.status === "OK") {
          this.success = this.$t("Offer declined.");

          this.$emit("offerDeclined", this.item.id);

          if (typeof this.refreshOfferHistory === "function") {
            this.refreshOfferHistory();
          }
          setTimeout(() => {
            this.closePreviewSkinModal();
          }, 400);
        } else {
          this.error = res.data.message || this.$t("Something went wrong.");
        }
      } catch (err) {
        if (err.response && err.response.status === 401) {
          this.$emit("unauthorized");
        }
        this.error = err.response.data?.message;
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/assets/styles/mixins" as *;
@use "@/assets/styles/media" as *;
@use "@/assets/styles/components/classes" as *;

.offer {
  &__inner {
    @media (min-width: 549.98px) {
      max-width: 506px;
    }
  }
  &__content {
    @media (min-width: 549.98px) {
      max-width: 385px;
      margin: 0 auto;
    }
  }
  &__title {
    text-align: center;
    &:not(:last-child) {
      @include adaptiveValue("margin-bottom", 20, 15);
    }
  }
  &__subtitle {
    text-align: center;
    color: var(--secondary-color);
    &:not(:last-child) {
      margin-bottom: 5px;
    }
    &_label {
      font-weight: 500;
      text-transform: capitalize;
      margin-bottom: 5px;
      display: block;
      text-align: center;
      color: var(--primary-color);
    }
  }
  &__about {
    &:not(:last-child) {
      @include adaptiveValue("margin-bottom", 20, 15);
    }
    @media (min-width: 549.98px) {
      display: flex;
      justify-content: center;
      gap: 10px;
    }
  }
  &__name {
    text-align: center;
    align-self: center;
    font-weight: 400;
    text-transform: uppercase;
  }

  /* Price Styling */
  &__prices {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 25px;
  }
  &__price-row {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  &__price-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    &_service {
      /* specific styles for service price if needed */
    }
  }
  &__service-price,
  &__price {
    display: flex;
    align-items: center;
    gap: 6px;
    &-number {
      &_regular {
        font-weight: 400;
      }
    }
    &-sumbol {
      font-weight: 400;
    }
    &-icon {
      @include adaptiveValue("min-width", 23, 18);
      @include adaptiveValue("height", 23, 18);
    }
  }

  &__actions {
    margin-top: 30px;
    @media (min-width: 549.98px) {
      display: flex;
      @include adaptiveValue("gap", 18, 8);
    }
  }
  &__success,
  &__error {
    margin-bottom: 8px;
    text-align: center;
    text-transform: capitalize;
  }
  &__error {
    color: #f1c40f;
  }
  &__success {
    color: var(--success-color);
  }
  &__submit {
    @media (min-width: 549.98px) {
      flex: 0 1 50%;
    }
    @media (max-width: 549.98px) {
      max-width: 300px;
      width: 100%;
      margin-left: auto;
      margin-right: auto;
      &:not(:last-child) {
        margin-bottom: 5px;
      }
    }
  }
  &__form {
    margin-bottom: 20px;
  }
  &__input {
    margin-bottom: 10px;
  }
  .input__field {
    width: 100%;
    height: 44px;
    border-radius: var(--border-radius-primary);
    background-color: var(--bg-secondary-color);
    border: 1px solid var(--bg-third-color);
    padding: 0px 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--primary-color);
    text-align: center;
    outline: transparent;
    transition: border 0.3s ease 0s;
    font-size: var(--font-size-base);
    appearance: none;
    cursor: pointer;
    &:focus {
      border: 1px solid var(--primary-color);
    }
  }
  .input {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    &__label {
      display: inline-block;
      font-weight: 500;
      text-transform: capitalize;
    }
  }
}
</style>
